
/*
 * NEMEAN NAVIGATOR
 * Berechnungskern v1.0
 *
 * Datei: assets/nemean-navigator-calc.js
 *
 * Enthält ausschließlich Berechnungen und
 * deren fachliche Erläuterungen.
 *
 * KEINE:
 * - HTML-Manipulation
 * - Shopify-Abhängigkeit
 * - Speicherung persönlicher Eingaben
 * - automatischen Produktempfehlungen
 *
 * Schnittstelle:
 * window.NemeanNavigatorCalc.calculate(inputs)
 *
 * Erwartete Eingaben:
 * {
 *   age: 30,
 *   height: 175,
 *   weight: 75,
 *   formula: "male",
 *   activity: 1.375,
 *   goal: "muscle",
 *   weightGoal: "maintain",
 *   protein: "reference"
 * }
 */

(function (window) {
  "use strict";

  var VERSION = "1.0";

  var ACTIVITY_FACTORS = [
    1.2,
    1.375,
    1.55,
    1.725
  ];

  var GOALS = [
    "muscle",
    "weight",
    "balance",
    "recover"
  ];

  var WEIGHT_GOALS = [
    "lose",
    "maintain",
    "gain"
  ];

  var PROTEIN_MODES = [
    "reference",
    "sport",
    "strength"
  ];

  function isNumberInRange(value, min, max) {
    return (
      typeof value === "number" &&
      Number.isFinite(value) &&
      value >= min &&
      value <= max
    );
  }

  function parseNumber(value) {
    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ""
    ) {
      return NaN;
    }

    return Number(value);
  }

  function round(value, digits) {
    var factor = Math.pow(10, digits || 0);
    return Math.round((value + Number.EPSILON) * factor)
      / factor;
  }

  function formatNumber(value, digits) {
    return new Intl.NumberFormat("de-DE", {
      minimumFractionDigits: digits || 0,
      maximumFractionDigits: digits || 0
    }).format(value);
  }

  function validationError(field, message) {
    var error = new Error(message);
    error.name = "NemeanValidationError";
    error.field = field;
    return error;
  }

  function normalizeInputs(raw) {
    if (!raw || typeof raw !== "object") {
      throw validationError(
        "general",
        "Bitte gib die benötigten Daten ein."
      );
    }

    var values = {
      age: parseNumber(raw.age),
      height: parseNumber(raw.height),
      weight: parseNumber(raw.weight),
      formula: raw.formula,
      activity: parseNumber(raw.activity),
      goal: raw.goal || "muscle",
      weightGoal: raw.weightGoal || "maintain",
      protein: raw.protein || "reference"
    };

    if (
      !Number.isInteger(values.age) ||
      !isNumberInRange(values.age, 19, 100)
    ) {
      throw validationError(
        "age",
        "Bitte gib ein Alter zwischen 19 und 100 Jahren ein."
      );
    }

    if (!isNumberInRange(values.height, 120, 230)) {
      throw validationError(
        "height",
        "Bitte gib eine Körpergröße zwischen 120 und 230 cm ein."
      );
    }

    if (!isNumberInRange(values.weight, 30, 350)) {
      throw validationError(
        "weight",
        "Bitte gib ein Körpergewicht zwischen 30 und 350 kg ein."
      );
    }

    if (
      values.formula !== "male" &&
      values.formula !== "female"
    ) {
      throw validationError(
        "formula",
        "Bitte wähle eine Berechnungsvariante aus."
      );
    }

    if (
      ACTIVITY_FACTORS.indexOf(values.activity) === -1
    ) {
      throw validationError(
        "activity",
        "Bitte wähle ein gültiges Aktivitätsniveau aus."
      );
    }

    if (GOALS.indexOf(values.goal) === -1) {
      throw validationError(
        "goal",
        "Bitte wähle ein gültiges Ziel aus."
      );
    }

    if (WEIGHT_GOALS.indexOf(values.weightGoal) === -1) {
      throw validationError(
        "weightGoal",
        "Bitte wähle ein gültiges Gewichtsziel aus."
      );
    }

    if (PROTEIN_MODES.indexOf(values.protein) === -1) {
      throw validationError(
        "protein",
        "Bitte wähle eine gültige Proteinberechnung aus."
      );
    }

    return values;
  }

  /*
   * KALORIEN
   *
   * Mifflin-St.-Jeor:
   * Männer:
   * 10 × kg + 6,25 × cm - 5 × Alter + 5
   *
   * Frauen:
   * 10 × kg + 6,25 × cm - 5 × Alter - 161
   *
   * Tagesbedarf = Ruheenergieverbrauch ×
   * vereinfachter Aktivitätsfaktor.
   *
   * Zieländerungen sind illustrative
   * Rechenannahmen, keine medizinischen
   * Empfehlungen.
   */

  function calculateCalories(values) {
    var adjustment = values.formula === "male"
      ? 5
      : -161;

    var resting =
      10 * values.weight +
      6.25 * values.height -
      5 * values.age +
      adjustment;

    var maintenance = resting * values.activity;

    var goalFactor = 1;
    var goalNote = "";

    if (values.goal === "muscle") {
      goalFactor = 1.05;

      goalNote =
        "Muskelaufbau: Beispielrechnung mit " +
        "5 % Energieüberschuss gegenüber dem " +
        "geschätzten Erhaltungsbedarf.";
    }

    if (values.goal === "weight") {
      if (values.weightGoal === "lose") {
        goalFactor = 0.90;

        goalNote =
          "Gewicht reduzieren: Beispielrechnung " +
          "mit 10 % Energiedefizit.";
      } else if (values.weightGoal === "gain") {
        goalFactor = 1.05;

        goalNote =
          "Gewicht zunehmen: Beispielrechnung " +
          "mit 5 % Energieüberschuss.";
      } else {
        goalFactor = 1;

        goalNote =
          "Gewicht halten: Orientierung am " +
          "geschätzten Erhaltungsbedarf.";
      }
    }

    if (values.goal === "balance") {
      goalNote =
        "Balance: Orientierung am geschätzten " +
        "Erhaltungsbedarf, ohne automatische " +
        "Kalorienänderung.";
    }

    if (values.goal === "recover") {
      goalNote =
        "Regeneration: Orientierung am geschätzten " +
        "Erhaltungsbedarf, ohne automatische " +
        "Kalorienänderung.";
    }

    var target = maintenance * goalFactor;

    if (
      !Number.isFinite(resting) ||
      !Number.isFinite(maintenance) ||
      !Number.isFinite(target) ||
      resting <= 0 ||
      maintenance <= 0 ||
      target <= 0
    ) {
      throw validationError(
        "general",
        "Aus diesen Angaben lässt sich kein " +
        "plausibler Energiebedarf berechnen."
      );
    }

    var warnings = [];

    if (target < 1200) {
      warnings.push(
        "Das rechnerische Kalorienziel ist " +
        "sehr niedrig. Bitte verwende diesen " +
        "Wert nicht ungeprüft als Ernährungsplan."
      );
    }

    var explanation =
      "Der geschätzte Ruheenergieverbrauch " +
      "beträgt " +
      formatNumber(resting) +
      " kcal pro Tag (Mifflin-St.-Jeor). " +
      "Mit dem ausgewählten Aktivitätsfaktor " +
      formatNumber(values.activity, 3) +
      " ergibt sich ein geschätzter " +
      "Gesamtenergiebedarf von " +
      formatNumber(maintenance) +
      " kcal pro Tag. " +
      "Der Zielfaktor beträgt " +
      formatNumber(goalFactor, 2) +
      ". Diese Faktoren sind Modellannahmen. " +
      "Dein tatsächlicher Verbrauch kann abweichen.";

    return {
      resting: resting,
      maintenance: maintenance,
      target: target,
      goalFactor: goalFactor,
      goalNote: goalNote,
      explanation: explanation,
      warnings: warnings
    };
  }

  /*
   * PROTEIN
   *
   * DGE-Referenz:
   * 19 bis unter 65: 0,8 g/kg
   * ab 65: 1,0 g/kg
   *
   * Bei BMI > 25:
   * Im Referenzmodus wird ein rechnerisches
   * Gewicht bei BMI 24,9 verwendet.
   *
   * Sportbeispiele:
   * 1,4 und 1,6 g/kg tatsächliches Gewicht.
   *
   * Diese Beispiele sind keine allgemeine
   * Verordnung und nicht für jede Person
   * individuell geeignet.
   */

  function calculateProtein(values) {
    var heightMeters = values.height / 100;

    var bmi =
      values.weight /
      (heightMeters * heightMeters);

    var referenceWeight =
      values.weight;

    var factor;
    var modeLabel;
    var usesAdjustedWeight = false;

    if (values.protein === "reference") {
      factor = values.age >= 65 ? 1.0 : 0.8;
      modeLabel = "Allgemeiner Referenzwert";

      if (bmi > 25) {
        referenceWeight =
          24.9 * heightMeters * heightMeters;

        usesAdjustedWeight = true;
      }
    } else if (values.protein === "sport") {
      factor = 1.4;
      modeLabel = "Sportlich aktiv";
    } else {
      factor = 1.6;
      modeLabel = "Krafttraining und Muskelaufbau";
    }

    var grams = referenceWeight * factor;

    if (
      !Number.isFinite(grams) ||
      grams <= 0
    ) {
      throw validationError(
        "general",
        "Der Proteinrichtwert konnte nicht " +
        "plausibel berechnet werden."
      );
    }

    var note;

    if (values.protein === "reference") {
      note =
        values.age >= 65
          ? "Die DGE nennt für gesunde Erwachsene " +
            "ab 65 Jahren einen Schätzwert von " +
            "1,0 g Protein je kg Bezugsgewicht " +
            "und Tag."
          : "Die DGE empfiehlt für gesunde " +
            "Erwachsene von 19 bis unter 65 Jahren " +
            "0,8 g Protein je kg Bezugsgewicht " +
            "und Tag.";
    } else {
      note =
        "Dies ist eine sportbezogene " +
        "Beispielrechnung. Der individuelle " +
        "Bedarf hängt unter anderem von " +
        "Trainingsumfang, Ziel und " +
        "gesundheitlichen Voraussetzungen ab.";
    }

    var explanation =
      modeLabel +
      ": " +
      formatNumber(referenceWeight, 1) +
      " kg Bezugsgewicht × " +
      formatNumber(factor, 1) +
      " g/kg = " +
      formatNumber(grams, 1) +
      " g Protein pro Tag.";

    if (usesAdjustedWeight) {
      explanation +=
        " Weil der BMI über 25 liegt, wird " +
        "im allgemeinen Referenzmodus ein " +
        "vereinfachtes Bezugsgewicht " +
        "entsprechend BMI 24,9 verwendet. " +
        "Dies ist eine rechnerische Annäherung, " +
        "keine individuelle Bestimmung des " +
        "Körpergewichts oder Proteinbedarfs.";
    }

    if (values.protein !== "reference") {
      explanation +=
        " Für dieses Sportbeispiel wurde " +
        "das angegebene tatsächliche " +
        "Körpergewicht verwendet.";
    }

    var warnings = [];

    if (
      values.protein !== "reference" &&
      bmi > 30
    ) {
      warnings.push(
        "Bei deutlich erhöhtem BMI kann " +
        "eine reine Berechnung anhand des " +
        "tatsächlichen Körpergewichts " +
        "ungeeignet sein. Der Sportwert " +
        "sollte individuell eingeordnet werden."
      );
    }

    return {
      grams: grams,
      factor: factor,
      referenceWeight: referenceWeight,
      bmi: bmi,
      usesAdjustedWeight: usesAdjustedWeight,
      mode: values.protein,
      note: note,
      explanation: explanation,
      warnings: warnings
    };
  }

  /*
   * MAKROS
   *
   * Berechnungsmodell:
   * - Grundlage: rechnerisches Kalorienziel
   * - Protein: Wert des Proteinrechners
   * - Fett: 30 % der Energie
   * - Kohlenhydrate: verbleibende Energie
   *
   * Vereinfachte Umrechnung:
   * Protein = 4 kcal/g
   * Fett = 9 kcal/g
   * Kohlenhydrate = 4 kcal/g
   *
   * Es wird kein negativer KH-Wert erzeugt.
   */

  function calculateMacros(calories, protein) {
    var energy = calories.target;

    var proteinGrams = protein.grams;

    var proteinEnergy = proteinGrams * 4;

    var fatEnergy = energy * 0.30;

    var fatGrams = fatEnergy / 9;

    var carbEnergy =
      energy -
      proteinEnergy -
      fatEnergy;

    var carbGrams = carbEnergy / 4;

    var proteinPercent =
      (proteinEnergy / energy) * 100;

    var fatPercent =
      (fatEnergy / energy) * 100;

    var carbPercent =
      (carbEnergy / energy) * 100;

    var valid =
      Number.isFinite(energy) &&
      Number.isFinite(proteinGrams) &&
      Number.isFinite(fatGrams) &&
      Number.isFinite(carbGrams) &&
      energy > 0 &&
      proteinGrams >= 0 &&
      fatGrams >= 0 &&
      carbGrams >= 0;

    if (!valid) {
      return {
        valid: false,
        calories: energy,
        proteinGrams: proteinGrams,
        fatGrams: null,
        carbGrams: null,
        proteinPercent: null,
        fatPercent: null,
        carbPercent: null,
        note:
          "Mit dieser Kombination aus " +
          "Kalorienziel und Proteinrichtwert " +
          "ergibt sich kein sinnvoller " +
          "vereinfachter Makroplan. Die " +
          "Kalorien- und Proteinberechnungen " +
          "bleiben davon unabhängig.",
        explanation:
          "Im gewählten Modell beanspruchen " +
          "Protein und der angesetzte " +
          "Fettanteil zusammen mehr Energie " +
          "als rechnerisch verfügbar ist."
      };
    }

    var note =
      "Beispielhafte Verteilung der täglichen " +
      "Energie auf Protein, Fett und " +
      "Kohlenhydrate. Diese Aufteilung ist " +
      "kein individuell optimierter " +
      "Ernährungsplan.";

    var explanation =
      "Kaloriengrundlage: " +
      formatNumber(energy) +
      " kcal. " +
      "Protein: " +
      formatNumber(proteinGrams, 1) +
      " g × 4 kcal/g = " +
      formatNumber(proteinEnergy) +
      " kcal. " +
      "Fett: 30 % der Energie = " +
      formatNumber(fatGrams, 1) +
      " g. " +
      "Kohlenhydrate: verbleibende Energie = " +
      formatNumber(carbGrams, 1) +
      " g. " +
      "Die gerundeten Anzeigen können " +
      "geringfügige Abweichungen ergeben.";

    var warnings = [];

    if (carbPercent < 10) {
      warnings.push(
        "Der verbleibende " +
        "Kohlenhydratanteil ist sehr niedrig. " +
        "Diese vereinfachte Verteilung " +
        "sollte nicht ungeprüft übernommen " +
        "werden."
      );
    }

    return {
      valid: true,
      calories: energy,
      proteinGrams: proteinGrams,
      fatGrams: fatGrams,
      carbGrams: carbGrams,
      proteinPercent: proteinPercent,
      fatPercent: fatPercent,
      carbPercent: carbPercent,
      note: note,
      explanation: explanation,
      warnings: warnings
    };
  }

  /*
   * ZENTRALE SCHNITTSTELLE
   *
   * Liefert sämtliche drei Berechnungen
   * mit denselben Eingabewerten.
   *
   * Die Anzeige entscheidet später,
   * welches Ergebnis sichtbar ist.
   */

  function calculate(rawInputs) {
    var values = normalizeInputs(rawInputs);

    var calories = calculateCalories(values);

    var protein = calculateProtein(values);

    var macros = calculateMacros(calories, protein);

    return {
      version: VERSION,
      inputs: values,
      calories: calories,
      protein: protein,
      macros: macros
    };
  }

  /*
   * NUR DIESE FUNKTION WIRD ÖFFENTLICH
   * FÜR UNSERE NAVIGATOR-OBERFLÄCHE.
   */

  window.NemeanNavigatorCalc = Object.freeze({
    version: VERSION,
    calculate: calculate
  });

})(window);
